import { format, getDate } from 'date-fns';
import { RRule, Weekday } from 'rrule';
import { getWhatDayOfTheMonth } from 'screens/oneOnOneRabbit/InstancePage/common/modals/CustomRecurrenceModal';

export const getRecurrenceFrequencyOptions = (interval: number) => [
  {
    label: interval > 1 ? 'Weeks' : 'Week',
    value: RRule.WEEKLY,
  },
  {
    label: interval > 1 ? 'Months' : 'Month',
    value: RRule.MONTHLY,
  },
  {
    label: interval > 1 ? 'Years' : 'Year',
    value: RRule.YEARLY,
  },
];

// Replace the existing numberToWords with this more comprehensive function
const getOrdinalSuffix = (n: number) => {
  const lastDigit = n % 10;
  const lastTwoDigits = n % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${n}th`;
  }

  switch (lastDigit) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

// Map day names to RRule constants
export const RRuleDayMap = {
  Sunday: RRule.SU,
  Monday: RRule.MO,
  Tuesday: RRule.TU,
  Wednesday: RRule.WE,
  Thursday: RRule.TH,
  Friday: RRule.FR,
  Saturday: RRule.SA,
} as const;

export const generateFrequencyOptions = (selectedDate: Date) => {
  const selectedDay = format(selectedDate, 'EEEE'); // e.g., "Friday"
  const { nthDayOfTheMonth } = getWhatDayOfTheMonth(selectedDate);

  const frequencies = [
    {
      label: `Weekly on ${selectedDay}`,
      value: new RRule({
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: [RRuleDayMap[selectedDay]],
      })
        .toString()
        .split('RRULE:')[1],
    },
    {
      label: `Biweekly on ${selectedDay}`,
      value: new RRule({
        freq: RRule.WEEKLY,
        interval: 2,
        byweekday: [RRuleDayMap[selectedDay]],
      })
        .toString()
        .split('RRULE:')[1],
    },
    {
      label: `Monthly on ${getOrdinalSuffix(nthDayOfTheMonth)} ${selectedDay}`,
      value: new RRule({
        freq: RRule.MONTHLY,
        interval: 1,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1],
    },
    {
      label: `Quarterly on ${getOrdinalSuffix(nthDayOfTheMonth)} ${selectedDay}`,
      value: new RRule({
        freq: RRule.MONTHLY,
        interval: 3, // Every 3 months for quarterly
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1],
    },
    {
      label: `Half yearly on ${getOrdinalSuffix(nthDayOfTheMonth)} ${selectedDay}`,
      value: new RRule({
        freq: RRule.MONTHLY,
        interval: 6, // Every 6 months for half-yearly
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1],
    },
    {
      label: `Yearly on ${format(selectedDate, 'do MMMM')}`,
      value: new RRule({
        freq: RRule.YEARLY,
        interval: 1,
        bymonth: selectedDate.getMonth() + 1, // getMonth() is 0-based, RRule months are 1-based
        bymonthday: getDate(selectedDate),
      })
        .toString()
        .split('RRULE:')[1],
    },
  ];

  return frequencies;
};

export const generateWeeklyCustomRecurrenceOptions = (selectedDate: Date) => {
  const day = format(selectedDate, 'EEEE'); // e.g., "Saturday"
  return [
    {
      label: day, // e.g., "Saturday"
      value: RRuleDayMap[day], // e.g., RRule.SA
    },
  ];
};

export const generateMonthlyCustomRecurrenceOptions = (date: Date) => {
  const { nthDayOfTheMonth, isLastNthDayOfTheMonth } = getWhatDayOfTheMonth(date);
  const selectedMonthDay = date.getDate();
  const selectedDay = RRule[format(date, 'EEEEEE').toUpperCase()] as Weekday;

  const RECURRENCE_MONTH_OPTIONS = [
    {
      label: `Monthly on day ${selectedMonthDay}`,
      value: selectedMonthDay,
      isByMonthDay: true,
    },
    {
      label: `Monthly on the ${getOrdinalSuffix(nthDayOfTheMonth)} ${format(date, 'EEEE')}`,
      value: selectedDay.nth(nthDayOfTheMonth),
    },
  ];

  if (nthDayOfTheMonth === 5) RECURRENCE_MONTH_OPTIONS.pop();

  if (isLastNthDayOfTheMonth) {
    RECURRENCE_MONTH_OPTIONS.push({
      label: `Monthly on the last ${format(date, 'EEEE')}`,
      value: selectedDay.nth(-1),
    });
  }

  return RECURRENCE_MONTH_OPTIONS;
};

export const generateYearlyCustomRecurrenceOptions = (selectedDate: Date) => {
  const day = getDate(selectedDate); // e.g., 29
  const month = format(selectedDate, 'MMMM'); // e.g., "March"
  const ordinalDay = getOrdinalSuffix(day); // e.g., "29th"
  return [
    {
      label: `Yearly on ${ordinalDay} ${month}`, // e.g., "Yearly on 29th March"
      value: `${day}-${selectedDate.getMonth() + 1}`, // e.g., "29-3" (day-month, 1-based month)
    },
  ];
};

export const generateDefaultRecurrenceRule = (selectedDate: Date) => {
  const selectedDay = format(selectedDate, 'EEEE');
  // e.g., "Weekly on Sunday"
  const rrule = new RRule({
    freq: RRule.WEEKLY,
    interval: 1,
    byweekday: [RRuleDayMap[selectedDay]],
  })
    .toString()
    .split('RRULE:')[1];

  return rrule;
};
